/**
 * News & Events Grid
 * Responsive card grid for news articles and event announcements
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { Calendar, User, ArrowRight, MapPin, Clock } from 'lucide-react';
import banner2 from '../../../assets/images/banner2.png';
import banner3 from '../../../assets/images/banner3.png';
import advert1 from '../../../assets/images/advert1.png';
import advert2 from '../../../assets/images/advert2.png';
import advert3 from '../../../assets/images/advert3.png';
import aboutImg from '../../../assets/images/aboutUs_image1.png';

// Demo data — swap with API integration when CMS backend is ready
export const newsEventsData = [
  {
    id: 'featured-1',
    type: 'news',
    category: 'Announcements',
    title: 'Annual General Meeting 2026 — Key Resolutions Passed',
    summary: 'Members gathered for the 21st Annual General Meeting where dividend rates, new loan products, and governance reforms were approved for the upcoming fiscal year.',
    date: 'May 20, 2026',
    author: 'Secretariat',
    image: banner2,
  },
  {
    id: 'news-1',
    type: 'news',
    category: 'Financial',
    title: 'New Reduced Interest Rates on Development Loans',
    summary: 'Effective June 1st, development loan interest rates drop to 10.5% p.a. for qualifying members with 5+ years of active savings.',
    date: 'May 18, 2026',
    author: 'Finance Committee',
    image: advert1,
  },
  {
    id: 'news-2',
    type: 'news',
    category: 'Community',
    title: 'Uhamiaji SACCOS Wins Cooperative Excellence Award 2026',
    summary: 'Recognized by the Tanzania Cooperative Development Commission for outstanding member service and financial performance.',
    date: 'May 15, 2026',
    author: 'Communications',
    image: advert2,
  },
  {
    id: 'event-1',
    type: 'event',
    category: 'Community',
    title: 'Financial Literacy Workshop for Members',
    summary: 'A one-day workshop covering personal finance, savings strategies, and investment basics tailored for public sector employees.',
    date: 'May 14, 2026',
    author: 'Training Department',
    image: banner3,
    eventDate: 'June 14, 2026',
    location: 'Dar es Salaam — Uhamiaji HQ',
    time: '09:00 AM — 03:00 PM',
  },
  {
    id: 'news-3',
    type: 'news',
    category: 'Institutional',
    title: 'Board of Directors Welcomes Two New Members',
    summary: 'Following the AGM elections, the board expands representation to include youth and gender-diverse perspectives in governance.',
    date: 'May 10, 2026',
    author: 'Governance Office',
    image: advert3,
  },
  {
    id: 'event-2',
    type: 'event',
    category: 'Financial',
    title: 'Q2 Investment & Savings Open Day',
    summary: 'Meet our financial advisors, review your portfolio, and explore new savings products in person at our headquarters.',
    date: 'May 8, 2026',
    author: 'Member Services',
    image: aboutImg,
    eventDate: 'July 5, 2026',
    location: 'Uhamiaji SACCOS Headquarters',
    time: '08:00 AM — 04:00 PM',
  },
];

const getCategoryColor = (cat) => {
  const palette = [
    'bg-blue-100 text-blue-700',
    'bg-green-100 text-green-700',
    'bg-orange-100 text-orange-700',
    'bg-purple-100 text-purple-700',
    'bg-red-100 text-red-700',
    'bg-teal-100 text-teal-700',
    'bg-indigo-100 text-indigo-700',
    'bg-pink-100 text-pink-700',
    'bg-cyan-100 text-cyan-700',
    'bg-amber-100 text-amber-700',
    'bg-lime-100 text-lime-700',
    'bg-sky-100 text-sky-700',
  ];
  if (!cat) return 'bg-gray-100 text-gray-700';
  let hash = 0;
  for (let i = 0; i < cat.length; i++) {
    hash = cat.charCodeAt(i) + ((hash << 5) - hash);
  }
  return palette[Math.abs(hash) % palette.length];
};

const NewsEventsGrid = ({ items }) => {
  if (!items || items.length === 0) {
    return (
      <section className="bg-gray-50 py-12">
        <div className="container mx-auto px-6 md:px-8 lg:px-12 text-center">
          <p className="text-gray-400 text-sm">No articles or events found matching your criteria.</p>
        </div>
      </section>
    );
  }

  return (
    <section className="bg-gray-50 py-8 md:py-10">
      <div className="container mx-auto px-6 md:px-8 lg:px-12">
        {/* Section Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-1 h-5 bg-[#ca9a37] rounded-full" />
            <h2 className="text-lg font-bold text-[#2f3951]">Latest Updates</h2>
          </div>
          <span className="text-xs text-gray-400">{items.length} result{items.length !== 1 ? 's' : ''}</span>
        </div>

        {/* Card Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {items.map((item) => {
            const isEvent = item.type === 'event';
            const badgeClass = getCategoryColor(item.category);

            return (
              <Link
                key={item.id}
                to={`/news-events/${item.id}`}
                className="group bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden transition-all duration-300 hover:-translate-y-1 hover:shadow-lg hover:border-[#ca9a37]/20"
              >
                {/* Thumbnail */}
                <div className="relative h-44 overflow-hidden">
                  <img
                    src={item.image || banner2}
                    alt={item.title}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute top-3 left-3 flex items-center gap-2">
                    <span className={`inline-block px-2.5 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide ${badgeClass}`}>
                      {item.category}
                    </span>
                    {isEvent && (
                      <span className="inline-block px-2.5 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide bg-[#ca9a37] text-white">
                        Event
                      </span>
                    )}
                  </div>
                </div>

                {/* Content */}
                <div className="p-5">
                  <h3 className="text-sm font-bold text-[#2f3951] mb-2 leading-snug group-hover:text-[#ca9a37] transition-colors line-clamp-2">
                    {item.title}
                  </h3>

                  <p className="text-gray-500 text-xs leading-relaxed mb-3 line-clamp-2">
                    {item.summary}
                  </p>

                  {/* Meta row */}
                  <div className="flex items-center justify-between text-[11px] text-gray-400">
                    <span className="flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {item.date}
                    </span>
                    <span className="flex items-center gap-1">
                      <User className="w-3 h-3" />
                      {item.category}
                    </span>
                  </div>

                  {/* Event-specific extras */}
                  {isEvent && (
                    <div className="mt-3 pt-3 border-t border-gray-50 space-y-1.5">
                      <div className="flex items-center gap-1.5 text-[11px] text-gray-500">
                        <Calendar className="w-3 h-3 text-[#ca9a37]" />
                        <span className="font-medium text-[#2f3951]">{item.eventDate}</span>
                      </div>
                      <div className="flex items-center gap-1.5 text-[11px] text-gray-500">
                        <MapPin className="w-3 h-3 text-[#ca9a37]" />
                        {item.location}
                      </div>
                      <div className="flex items-center gap-1.5 text-[11px] text-gray-500">
                        <Clock className="w-3 h-3 text-[#ca9a37]" />
                        {item.time}
                      </div>
                    </div>
                  )}

                  {/* CTA */}
                  <div className="mt-4 flex items-center text-[#ca9a37] text-xs font-semibold group-hover:underline">
                    {isEvent ? 'View Event' : 'Read More'}
                    <ArrowRight className="w-3.5 h-3.5 ml-1 transition-transform group-hover:translate-x-0.5" />
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default NewsEventsGrid;
